import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label,
  Select,
  Option,
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import { parseToNumber, formatNumberWithComma, formatDateToDDMMYYYY, rightAlignedNumberStyle, formattedNumberCell } from '../utils/validator';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

const WarehouseList = () => {
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [centers, setCenters] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'code',
    sortDirection: 'asc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    warehouseType: '',
    areaSqm: 0,
    capacityCubicMeter: 0,
    heightMeter: 0,
    temperatureMin: 0,
    temperatureMax: 0,
    humidityMin: 0,
    humidityMax: 0,
    aislesCount: 0,
    racksCount: 0,
    isActive: true,
    hasDock: true,
    dockCount: 0,
    currentUsagePercentage: 0,
    centerId: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchWarehouses = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const url = `warehouses?page=${page}&size=${pageSize}`;
      const response = await axiosInstance.get(url);
      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los almacenes: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const fetchCenters = useCallback(async () => {
    setLoadingCenters(true);
    try {
      const response = await axiosInstance.get('centers');
      if (response.data.records.code === "SUCCESS") {
        setCenters(response.data.records.data);
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los centros: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoadingCenters(false);
    }
  }, []);

  useEffect(() => {
    fetchWarehouses(currentPage);
  }, [currentPage, fetchWarehouses]);

  useEffect(() => {
    if (showCreateDialog || showEditDialog) {
      fetchCenters();
    }
  }, [showCreateDialog, showEditDialog, fetchCenters]);

  useEffect(() => {
    let filtered = [...data];
    
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(warehouse => 
        warehouse.code?.toLowerCase().includes(lowerCaseSearchTerm) ||
        warehouse.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
        warehouse.warehouseType?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
    
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      sortBy: column,
      sortDirection: prevConfig.sortBy === column && prevConfig.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (event) => {
    if (!hasPermission('warehouses', 'update')) {
      return;
    }
    
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const warehouse = detail.row.original;
      setSelectedWarehouse(warehouse);
      setFormData({
        code: warehouse.code || '',
        name: warehouse.name || '',
        warehouseType: warehouse.warehouseType || '',
        areaSqm: warehouse.areaSqm || 0,
        capacityCubicMeter: warehouse.capacityCubicMeter || 0,
        heightMeter: warehouse.heightMeter || 0,
        temperatureMin: warehouse.temperatureMin || 0,
        temperatureMax: warehouse.temperatureMax || 0,
        humidityMin: warehouse.humidityMin || 0,
        humidityMax: warehouse.humidityMax || 0,
        aislesCount: warehouse.aislesCount || 0,
        racksCount: warehouse.racksCount || 0,
        isActive: warehouse.isActive !== undefined ? warehouse.isActive : true,
        hasDock: warehouse.hasDock !== undefined ? warehouse.hasDock : true,
        dockCount: warehouse.dockCount || 0,
        currentUsagePercentage: warehouse.currentUsagePercentage || 0,
        centerId: warehouse.centerId || '',
      });
      setShowEditDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleCreateClick = () => {
    if (!hasPermission('warehouses', 'create')) {
      return;
    }
    
    setFormData({
      code: '',
      name: '',
      warehouseType: '',
      areaSqm: 0,
      capacityCubicMeter: 0,
      heightMeter: 0,
      temperatureMin: 0,
      temperatureMax: 0,
      humidityMin: 0,
      humidityMax: 0,
      aislesCount: 0,
      racksCount: 0,
      isActive: true,
      hasDock: true,
      dockCount: 0,
      currentUsagePercentage: 0,
      centerId: '',
    });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleEditClick = (warehouse) => {
    if (!hasPermission('warehouses', 'update')) {
      return;
    }
    
    setSelectedWarehouse(warehouse);
    setFormData({
      code: warehouse.code || '',
      name: warehouse.name || '',
      warehouseType: warehouse.warehouseType || '',
      areaSqm: warehouse.areaSqm || 0,
      capacityCubicMeter: warehouse.capacityCubicMeter || 0,
      heightMeter: warehouse.heightMeter || 0,
      temperatureMin: warehouse.temperatureMin || 0,
      temperatureMax: warehouse.temperatureMax || 0,
      humidityMin: warehouse.humidityMin || 0,
      humidityMax: warehouse.humidityMax || 0,
      aislesCount: warehouse.aislesCount || 0,
      racksCount: warehouse.racksCount || 0,
      isActive: warehouse.isActive !== undefined ? warehouse.isActive : true,
      hasDock: warehouse.hasDock !== undefined ? warehouse.hasDock : true,
      dockCount: warehouse.dockCount || 0,
      currentUsagePercentage: warehouse.currentUsagePercentage || 0,
      centerId: warehouse.centerId || '',
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleDeleteClick = (warehouse) => {
    if (!hasPermission('warehouses', 'delete')) {
      return;
    }
    
    setSelectedWarehouse(warehouse);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedWarehouse) return;
    
    try {
      await axiosInstance.delete(`warehouses/${selectedWarehouse.id}`);
      fetchWarehouses(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error al eliminar el bodega: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleNumberInputChange = (event) => {
    const { name, value } = event.target;
    // Permitir entrada con formato (comas y puntos)
    // y convertir al valor numérico real para el estado
    const numericValue = parseToNumber(value);
    
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (event) => {
    const { name } = event.target;
    const value = event.detail.selectedOption.getAttribute('value');
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name) {
      errors.name = 'El nombre es obligatorio';
    }
    
    if (!formData.warehouseType) {
      errors.warehouseType = 'El tipo de bodega es obligatorio';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const response = await axiosInstance.post('warehouses', formData);
      if (response.status === 201 || response.status === 200) {
        fetchWarehouses(currentPage);
        setShowCreateDialog(false);
      } else {
        setError('Error al crear el bodega: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al crear el bodega: ' + (err.message || 'Error desconocido'));
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedWarehouse) return;
    
    try {
      const response = await axiosInstance.put(`warehouses/${selectedWarehouse.id}`, formData);
      if (response.status === 200) {
        fetchWarehouses(currentPage);
        setShowEditDialog(false);
      } else {
        setError('Error al actualizar el bodega: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al actualizar el bodega: ' + (err.message || 'Error desconocido'));
    }
  };

  const columns = [
    {
      Header: 'Código',
      accessor: 'code',
      headerProps: {
        onClick: () => handleSort('code')
      },
      className: sortConfig.sortBy === 'code' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Nombre',
      accessor: 'name',
      headerProps: {
        onClick: () => handleSort('name')
      },
      className: sortConfig.sortBy === 'name' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Tipo',
      accessor: 'warehouseType',
      headerProps: {
        onClick: () => handleSort('warehouseType')
      },
      className: sortConfig.sortBy === 'warehouseType' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Área (m²)',
      accessor: 'areaSqm',
      Cell: ({ value }) => <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value) || '0'}</Text>,
      headerProps: {
        onClick: () => handleSort('areaSqm')
      },
      hAlign: "End",
      className: sortConfig.sortBy === 'areaSqm' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Capacidad (m³)',
      accessor: 'capacityCubicMeter',
      Cell: ({ value }) => <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value) || '0'}</Text>,
      headerProps: {
        onClick: () => handleSort('capacityCubicMeter')
      },
      hAlign: "End",
      className: sortConfig.sortBy === 'capacityCubicMeter' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Estado',
      accessor: 'isActive',
      Cell: ({ value }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon 
            name={value ? "status-positive" : "status-negative"} 
            style={{ marginRight: '8px', color: value ? '#28a745' : '#dc3545' }} 
          />
          <Text>{value ? 'Activo' : 'Inactivo'}</Text>
        </div>
      ),
      headerProps: {
        onClick: () => handleSort('isActive')
      },
      className: sortConfig.sortBy === 'isActive' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: 'Acciones',
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('warehouses', 'update') && (
            <Button 
              icon="edit" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row.original);
              }}
            />
          )}
          {hasPermission('warehouses', 'delete') && (
            <Button 
              icon="delete" 
              design="Transparent" 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de bodegas')}</Title>
        
        {error && (
          <div style={{ color: 'red', margin: '1rem 0' }}>
            {error}
          </div>
        )}
        
        <Bar
          startContent={
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Input
                placeholder={autoT('Buscar bodega...')}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: '250px' }}
              />
            </div>
          }
          endContent={
            hasPermission('warehouses', 'create') && (
              <Button 
                icon="add" 
                design="Emphasized"
                onClick={handleCreateClick}
              >
                {autoT('Nuevo Bodega')}
              </Button>
            )
          }
        />
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('warehouses', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                icon="navigation-left-arrow" 
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                Anterior
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button 
                icon="navigation-right-arrow" 
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                iconEnd
              >
                Siguiente
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Dialog para crear/editar bodega */}
      <Dialog
        open={showCreateDialog || showEditDialog}
        onClose={() => showCreateDialog ? setShowCreateDialog(false) : setShowEditDialog(false)}
        header={
          <Bar
            startContent={
              <Title level="H4">
                {showCreateDialog ? autoT('Crear Nuevo Bodega') : autoT('Editar Bodega')}
              </Title>
            }
          />
        }
        footer={
          <Bar
            endContent={
              <>
                <Button 
                  design="Emphasized" 
                  onClick={showCreateDialog ? handleCreateSubmit : handleEditSubmit}
                >
                  {autoT('Guardar')}
                </Button>
                <Button 
                  design="Transparent" 
                  onClick={() => showCreateDialog ? setShowCreateDialog(false) : setShowEditDialog(false)}
                >
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        style={{ width: '800px' }}
      >
        <Form style={{ padding: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Columna 1 */}
            <FormGroup titleText={autoT('Información Básica')}>
              <FormItem labelContent={<Label>{autoT('Nombre')}</Label>}>
                <Input 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  valueState={formErrors.name ? "Error" : "None"}
                  valueStateMessage={formErrors.name}
                  required
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Tipo Bodega')}</Label>}>
                <Input 
                  name="warehouseType" 
                  value={formData.warehouseType}
                  onChange={handleInputChange}
                  valueState={formErrors.warehouseType ? "Error" : "None"}
                  valueStateMessage={formErrors.warehouseType}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Centro')}</Label>}>
                {loadingCenters ? (
                  <BusyIndicator active size="Small" />
                ) : (
                  <Select
                    name="centerId"
                    onChange={handleSelectChange}
                    value={formData.centerId}
                    valueState={formErrors.centerId ? "Error" : "None"}
                    valueStateMessage={formErrors.centerId}
                  >
                    <Option value="">{autoT('Seleccione un centro')}</Option>
                    {centers.map((center) => (
                      <Option key={center.id} value={center.id}>
                        {center.name} ({center.code})
                      </Option>
                    ))}
                  </Select>
                )}
              </FormItem>
            </FormGroup>

            {/* Columna 2 */}
            <FormGroup titleText={autoT('Estado y Configuración')}>
              <FormItem labelContent={<Label>{autoT('Estado')}</Label>}>
                <Button 
                  icon={formData.isActive ? "status-positive" : "status-negative"}
                  design="Transparent"
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                  style={{ color: formData.isActive ? '#28a745' : '#dc3545' }}
                >
                  {formData.isActive ? autoT('Activo') : autoT('Inactivo')}
                </Button>
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Tiene Dock')}</Label>}>
                <Button 
                  icon={formData.hasDock ? "status-positive" : "status-negative"}
                  design="Transparent"
                  onClick={() => setFormData(prev => ({ ...prev, hasDock: !prev.hasDock }))}
                  style={{ color: formData.hasDock ? '#28a745' : '#dc3545' }}
                >
                  {formData.hasDock ? autoT('Sí') : autoT('No')}
                </Button>
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Uso Actual (%)')}</Label>}>
                <Input 
                  type="text"
                  name="currentUsagePercentage" 
                  value={formatNumberWithComma(formData.currentUsagePercentage)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
            </FormGroup>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            {/* Columna 3 */}
            <FormGroup titleText={autoT('Dimensiones')}>
              <FormItem labelContent={<Label>{autoT('Área (m²)')}</Label>}>
                <Input 
                  type="text"
                  name="areaSqm" 
                  value={formatNumberWithComma(formData.areaSqm)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Capacidad (m³)')}</Label>}>
                <Input 
                  type="text"
                  name="capacityCubicMeter" 
                  value={formatNumberWithComma(formData.capacityCubicMeter)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Altura (m)')}</Label>}>
                <Input 
                  type="text"
                  name="heightMeter" 
                  value={formatNumberWithComma(formData.heightMeter)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
            </FormGroup>

            {/* Columna 4 */}
            <FormGroup titleText={autoT('Configuración de espacio')}>
              <FormItem labelContent={<Label>{autoT('Pasillos')}</Label>}>
                <Input 
                  type="text"
                  name="aislesCount" 
                  value={formatNumberWithComma(formData.aislesCount)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Estantes')}</Label>}>
                <Input 
                  type="text"
                  name="racksCount" 
                  value={formatNumberWithComma(formData.racksCount)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Docks')}</Label>}>
                <Input 
                  type="text"
                  name="dockCount" 
                  value={formatNumberWithComma(formData.dockCount)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
            </FormGroup>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
            {/* Columna 5 */}
            <FormGroup titleText={autoT('Control de temperatura')}>
              <FormItem labelContent={<Label>{autoT('Temperatura Mínima (°C)')}</Label>}>
                <Input 
                  type="text"
                  name="temperatureMin" 
                  value={formatNumberWithComma(formData.temperatureMin)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Temperatura máxima (°C)')}</Label>}>
                <Input 
                  type="text"
                  name="temperatureMax" 
                  value={formatNumberWithComma(formData.temperatureMax)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
            </FormGroup>

            {/* Columna 6 */}
            <FormGroup titleText={autoT('Control de humedad')}>
              <FormItem labelContent={<Label>{autoT('Humedad mínima (%)')}</Label>}>
                <Input 
                  type="text"
                  name="humidityMin" 
                  value={formatNumberWithComma(formData.humidityMin)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
              <FormItem labelContent={<Label>{autoT('Humedad máxima (%)')}</Label>}>
                <Input 
                  type="text"
                  name="humidityMax" 
                  value={formatNumberWithComma(formData.humidityMax)}
                  onChange={handleNumberInputChange}
                  style={{ textAlign: 'right' }}
                />
              </FormItem>
            </FormGroup>
          </div>
        </Form>
      </Dialog>

      {/* Confirmación de eliminación */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title="Confirmar eliminación"
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        ¿Está seguro que desea eliminar el bodega <strong>{selectedWarehouse?.name}</strong>? Esta acción no se puede deshacer.
      </MessageBox>
    </>
  );
};

export default withPermission(WarehouseList, 'warehouses', 'listView');
